import deepmerge from '@mui/utils/deepmerge';
import { Theme } from '@repo/shared/types/dashboard';
import { type RfEmbedSdkConfig, type RfEmbedSdkInstanceOverrideConfig, type RfEmbedSdkAiOverviewConfig, type RfEmbedSdkDisplayConfig, type RfEmbedSdkFloatingLauncherConfig, type RfEmbedSdkInlineTriggerConfig, type RfEmbedSdkSearchConfig, type RfEmbedSdkSearchAction, type RfEmbedSdkSearchWidgetConfig, RfEmbedSdkAnchorPosition, RfEmbedSdkDisplayType, RfEmbedSdkInlineTriggerVariant, RfEmbedSdkSearchActionType, type RfEmbedSdkOptionalStringFieldKey, type RfEmbedSdkOptionalBooleanFieldKey, type RfEmbedSdkLegacySearchDisplay } from '@repo/shared/types/core/widget';
import { logger } from '@repo/shared/utils/core/logger';

export const validateRfEmbedSdkConfig = (config: RfEmbedSdkConfig, currentDisplayType?: string): boolean => {
	if (typeof window === 'undefined') {
		void logger.error('[Rapidflare] Cannot validate config in server-side environment. This function must be called in a browser environment.');
		return false;
	}
	if (typeof config?.apiKey !== 'string' || config?.apiKey?.length === 0) {
		void logger.error('[Rapidflare] Missing required field: apiKey');
		return false;
	}
	if (config?.id !== undefined && (typeof config?.id !== 'string' || config?.id?.trim?.()?.length === 0)) {
		void logger.error('[Rapidflare] Invalid id: must be a non-empty string when provided.');
		return false;
	}
	if (typeof config?.display !== 'object' || config?.display === null) {
		void logger.error('[Rapidflare] Missing required field: display');
		return false;
	}
	const displayType = (config?.display as RfEmbedSdkDisplayConfig)?.type;
	if (typeof displayType !== 'string' || (Object.values<RfEmbedSdkDisplayType>(RfEmbedSdkDisplayType) as string[]).indexOf(displayType) === -1) {
		void logger.error(`[Rapidflare] Invalid display.type: "${String(displayType)}". Must be one of: ${Object.values<RfEmbedSdkDisplayType>(RfEmbedSdkDisplayType).join(', ')}`);
		return false;
	}
	if (typeof config?.theme === 'string' && config?.theme?.length > 0 && Object.values<Theme>(Theme)?.indexOf?.(config.theme) === -1) {
		void logger.error(`[Rapidflare] Invalid theme: "${config.theme}". Must be one of: ${Object.values<Theme>(Theme).join(', ')}`);
		return false;
	}
	if (config?.autoSubmit !== undefined && typeof config?.autoSubmit !== 'boolean') {
		void logger.error('[Rapidflare] Invalid autoSubmit: must be a boolean when provided.');
		return false;
	}
	if (config?.doNotTrack !== undefined && typeof config?.doNotTrack !== 'boolean') {
		void logger.error('[Rapidflare] Invalid doNotTrack: must be a boolean when provided.');
		return false;
	}
	if (typeof currentDisplayType === 'string' && currentDisplayType?.length > 0 && displayType !== currentDisplayType) {
		void logger.error(`[Rapidflare] Cannot change display.type from '${currentDisplayType}' to '${displayType}' via ref.update(). Call ref.destroy() and re-initialize with the new type instead.`);
		return false;
	}
	if ([RfEmbedSdkDisplayType.FULL_PAGE, RfEmbedSdkDisplayType.SEARCH, RfEmbedSdkDisplayType.AI_OVERVIEW].includes(displayType as RfEmbedSdkDisplayType)) {
		const display = config?.display as { container?: HTMLElement | string; };
		const container = display?.container;
		const hasContainer = (typeof container === 'string' && container?.length > 0) || (container instanceof HTMLElement);
		if (!hasContainer) {
			void logger.error(`[Rapidflare] Missing required field: display.container for type "${displayType}"`);
			return false;
		}
	}
	if (displayType === RfEmbedSdkDisplayType.FLOATING_LAUNCHER) {
		const display = config?.display as RfEmbedSdkFloatingLauncherConfig;
		const position = display?.position;
		const trigger = display?.trigger;
		const dragContainer = display?.dragContainer;
		if (position !== undefined && typeof position !== 'string') {
			void logger.error('[Rapidflare] Invalid display.position for type "floating-launcher": must be a string when provided.');
			return false;
		}
		if (typeof position === 'string' && position?.length > 0 && (Object.values<RfEmbedSdkAnchorPosition>(RfEmbedSdkAnchorPosition) as string[])?.indexOf?.(position) === -1) {
			void logger.error(`[Rapidflare] Invalid display.position: "${position}". Must be one of: ${(Object.values<RfEmbedSdkAnchorPosition>(RfEmbedSdkAnchorPosition) as string[]).join(', ')}`);
			return false;
		}
		if (trigger !== undefined) {
			if (typeof trigger === 'string') {
				if (trigger?.length === 0) {
					void logger.error('[Rapidflare] Invalid display.trigger for type "floating-launcher": empty string provided. Provide a non-empty CSS selector string, or omit to use the default trigger bubble.');
					return false;
				}
			} else if (!(trigger instanceof HTMLElement) && !(trigger instanceof SVGElement)) {
				void logger.error('[Rapidflare] Invalid display.trigger for type "floating-launcher": must be a non-empty CSS selector string, HTMLElement, or SVGElement when provided.');
				return false;
			}
		}
		if (dragContainer !== undefined && typeof dragContainer !== 'string') {
			void logger.error('[Rapidflare] Invalid display.dragContainer for type "floating-launcher": must be a string when provided.');
			return false;
		}
		if (typeof dragContainer === 'string' && dragContainer?.length === 0) {
			void logger.error('[Rapidflare] Invalid display.dragContainer: empty string provided. Provide a non-empty CSS selector string, or omit to disable drag container bounds.');
			return false;
		}
		if (!validateOptionalDisplayBoolean(RfEmbedSdkDisplayType.FLOATING_LAUNCHER, display, 'overlay')) {
			return false;
		}
		if (!validateOptionalDisplayBoolean(RfEmbedSdkDisplayType.FLOATING_LAUNCHER, display, 'fullWidthOnMaximize')) {
			return false;
		}
		if (!validateOptionalDisplayString(RfEmbedSdkDisplayType.FLOATING_LAUNCHER, display, 'titleText')) {
			return false;
		}
		if (!validateOptionalDisplayString(RfEmbedSdkDisplayType.FLOATING_LAUNCHER, display, 'titleLogo')) {
			return false;
		}
	}
	if (displayType === RfEmbedSdkDisplayType.INLINE_TRIGGER) {
		const display = config?.display as RfEmbedSdkInlineTriggerConfig;
		const trigger = display?.trigger;
		const variant = display?.variant;
		if (trigger === undefined) {
			void logger.error('[Rapidflare] Missing required field: display.trigger for type "inline-trigger". Provide a CSS selector string or HTMLElement.');
			return false;
		}
		if (typeof trigger === 'string') {
			if (trigger?.length === 0) {
				void logger.error('[Rapidflare] Invalid display.trigger for type "inline-trigger": empty string provided. Provide a non-empty CSS selector string or HTMLElement.');
				return false;
			}
		} else if (!(trigger instanceof HTMLElement)) {
			void logger.error('[Rapidflare] Invalid display.trigger for type "inline-trigger": must be a non-empty CSS selector string or HTMLElement.');
			return false;
		}
		if (variant !== undefined && typeof variant !== 'string') {
			void logger.error('[Rapidflare] Invalid display.variant for type "inline-trigger": must be a string when provided.');
			return false;
		}
		if (typeof variant === 'string' && variant?.length > 0 && (Object.values<RfEmbedSdkInlineTriggerVariant>(RfEmbedSdkInlineTriggerVariant) as string[]).indexOf(variant) === -1) {
			void logger.error(`[Rapidflare] Invalid display.variant: "${variant}". Must be one of: ${Object.values<RfEmbedSdkInlineTriggerVariant>(RfEmbedSdkInlineTriggerVariant).join(', ')}`);
			return false;
		}
		if (!validateOptionalDisplayBoolean(RfEmbedSdkDisplayType.INLINE_TRIGGER, display, 'overlay')) {
			return false;
		}
		if (!validateOptionalDisplayBoolean(RfEmbedSdkDisplayType.INLINE_TRIGGER, display, 'responsive')) {
			return false;
		}
		if (!validateOptionalDisplayBoolean(RfEmbedSdkDisplayType.INLINE_TRIGGER, display, 'fullWidthOnMaximize')) {
			return false;
		}
		if (!validateOptionalDisplayString(RfEmbedSdkDisplayType.INLINE_TRIGGER, display, 'titleText')) {
			return false;
		}
		if (!validateOptionalDisplayString(RfEmbedSdkDisplayType.INLINE_TRIGGER, display, 'titleLogo')) {
			return false;
		}
	}
	if (displayType === RfEmbedSdkDisplayType.SEARCH) {
		const display = config?.display as RfEmbedSdkSearchConfig;
		const minRows = display?.minRows;
		const maxRows = display?.maxRows;
		if (minRows !== undefined && (typeof minRows !== 'number' || !Number.isFinite(minRows) || minRows < 1)) {
			void logger.error(`[Rapidflare] Invalid display.minRows: "${String(minRows)}". Must be a finite number >= 1.`);
			return false;
		}
		if (maxRows !== undefined && (typeof maxRows !== 'number' || !Number.isFinite(maxRows) || maxRows < 1)) {
			void logger.error(`[Rapidflare] Invalid display.maxRows: "${String(maxRows)}". Must be a finite number >= 1.`);
			return false;
		}
		if (typeof minRows === 'number' && typeof maxRows === 'number' && maxRows < minRows) {
			void logger.error(`[Rapidflare] Invalid display.maxRows (${maxRows}) must be >= display.minRows (${minRows}).`);
			return false;
		}
		if (!validateOptionalDisplayString(RfEmbedSdkDisplayType.SEARCH, display as RfEmbedSdkLegacySearchDisplay, 'searchUrl')) {
			return false;
		}
		if (!validateOptionalDisplayBoolean(RfEmbedSdkDisplayType.SEARCH, display as RfEmbedSdkLegacySearchDisplay, 'searchNewTab')) {
			return false;
		}
		if (!validateOptionalDisplayString(RfEmbedSdkDisplayType.SEARCH, display, 'placeholder')) {
			return false;
		}
		if (!validateOptionalDisplayString(RfEmbedSdkDisplayType.SEARCH, display, 'starterPromptHeaderText')) {
			return false;
		}
		if (!validateOptionalDisplayBoolean(RfEmbedSdkDisplayType.SEARCH, display, 'autoFocus')) {
			return false;
		}
		if (!validateOptionalDisplayBoolean(RfEmbedSdkDisplayType.SEARCH, display, 'disableKeyboardTrigger')) {
			return false;
		}
		if (!validateOptionalDisplayBoolean(RfEmbedSdkDisplayType.SEARCH, display, 'aiSuggestionsDisabled')) {
			return false;
		}
		if (!validateOptionalDisplayBoolean(RfEmbedSdkDisplayType.SEARCH, display, 'starterPromptsDisabled')) {
			return false;
		}
		if (!validateOptionalDisplayBoolean(RfEmbedSdkDisplayType.SEARCH, display, 'starterPromptAutoSubmit')) {
			return false;
		}
		if (!validateSearchAction(display?.action)) {
			return false;
		}
	}
	if (displayType === RfEmbedSdkDisplayType.AI_OVERVIEW) {
		if (typeof config?.prompt !== 'string' || config?.prompt?.length === 0) {
			void logger.error('[Rapidflare] display.type "ai-overview" requires prompt to be a string.');
			return false;
		}
		const display = config?.display as RfEmbedSdkAiOverviewConfig;
		if (!validateOptionalDisplayString(RfEmbedSdkDisplayType.AI_OVERVIEW, display, 'titleText')) {
			return false;
		}
		if (!validateOptionalDisplayString(RfEmbedSdkDisplayType.AI_OVERVIEW, display, 'titleLogo')) {
			return false;
		}
		if (!validateOptionalDisplayString(RfEmbedSdkDisplayType.AI_OVERVIEW, display, 'ctaText')) {
			return false;
		}
		if (!validateOptionalDisplayString(RfEmbedSdkDisplayType.AI_OVERVIEW, display, 'ctaBackground')) {
			return false;
		}
		if (!validateOptionalDisplayString(RfEmbedSdkDisplayType.AI_OVERVIEW, display, 'ctaColor')) {
			return false;
		}
		if (!validateOptionalDisplayString(RfEmbedSdkDisplayType.AI_OVERVIEW, display, 'ctaUrl')) {
			return false;
		}
		if (!validateOptionalDisplayBoolean(RfEmbedSdkDisplayType.AI_OVERVIEW, display, 'ctaNewTab')) {
			return false;
		}
	}
	return true;
};

const validateSearchAction = (action: RfEmbedSdkSearchAction | undefined): boolean => {
	if (action === undefined) {
		return true;
	}
	if (typeof action !== 'object' || action === null) {
		void logger.error('[Rapidflare] Invalid display.action for type "search": must be an object when provided.');
		return false;
	}
	// `action.type` is typed as the valid union, but a customer can still pass anything at runtime. Read
	// it into a widened string so we can reject unknown values without TS narrowing `action` to `never`.
	const actionType: string = action.type;
	if (actionType !== RfEmbedSdkSearchActionType.EXTERNAL_URL && actionType !== RfEmbedSdkSearchActionType.WIDGET) {
		void logger.error(`[Rapidflare] Invalid display.action.type: "${actionType}". Must be one of: ${RfEmbedSdkSearchActionType.EXTERNAL_URL}, ${RfEmbedSdkSearchActionType.WIDGET}`);
		return false;
	}
	if (action.type === RfEmbedSdkSearchActionType.EXTERNAL_URL) {
		if (typeof action.url !== 'string' || action.url?.trim?.()?.length === 0) {
			void logger.error('[Rapidflare] Invalid display.action for type "external-url": url must be a non-empty string.');
			return false;
		}
		if (action.newTab !== undefined && typeof action.newTab !== 'boolean') {
			void logger.error('[Rapidflare] Invalid display.action.newTab for type "external-url": must be a boolean when provided.');
			return false;
		}
		return true;
	}
	return validateSearchWidgetAction(action.widget);
};

const validateSearchWidgetAction = (widget: RfEmbedSdkSearchWidgetConfig | undefined): boolean => {
	if (typeof widget !== 'object' || widget === null || widget === undefined) {
		void logger.error('[Rapidflare] Invalid display.action for type "widget": widget must be an object.');
		return false;
	}
	// See note in validateSearchAction: widen the discriminant to reject runtime-invalid types safely.
	const widgetType: string = widget.type;
	if (widgetType !== RfEmbedSdkDisplayType.FLOATING_LAUNCHER && widgetType !== RfEmbedSdkDisplayType.INLINE_TRIGGER) {
		void logger.error(`[Rapidflare] Invalid display.action.widget.type: "${widgetType}". Must be one of: ${RfEmbedSdkDisplayType.FLOATING_LAUNCHER}, ${RfEmbedSdkDisplayType.INLINE_TRIGGER}`);
		return false;
	}
	if (widget.id !== undefined && (typeof widget.id !== 'string' || widget.id?.trim?.()?.length === 0)) {
		void logger.error('[Rapidflare] Invalid display.action.widget.id: must be a non-empty string when provided.');
		return false;
	}
	if (widget.type === RfEmbedSdkDisplayType.FLOATING_LAUNCHER) {
		const position = widget.position;
		if (typeof position === 'string' && position.length > 0 && (Object.values<RfEmbedSdkAnchorPosition>(RfEmbedSdkAnchorPosition) as string[]).indexOf(position) === -1) {
			void logger.error(`[Rapidflare] Invalid display.action.widget.position: "${position}". Must be one of: ${(Object.values<RfEmbedSdkAnchorPosition>(RfEmbedSdkAnchorPosition) as string[]).join(', ')}`);
			return false;
		}
	}
	if (widget.type === RfEmbedSdkDisplayType.INLINE_TRIGGER) {
		const variant = widget.variant;
		if (typeof variant === 'string' && variant.length > 0 && (Object.values<RfEmbedSdkInlineTriggerVariant>(RfEmbedSdkInlineTriggerVariant) as string[]).indexOf(variant) === -1) {
			void logger.error(`[Rapidflare] Invalid display.action.widget.variant: "${variant}". Must be one of: ${(Object.values<RfEmbedSdkInlineTriggerVariant>(RfEmbedSdkInlineTriggerVariant) as string[]).join(', ')}`);
			return false;
		}
		if (widget.responsive !== undefined && typeof widget.responsive !== 'boolean') {
			void logger.error('[Rapidflare] Invalid display.action.widget.responsive: must be a boolean when provided.');
			return false;
		}
	}
	if (widget.overlay !== undefined && typeof widget.overlay !== 'boolean') {
		void logger.error('[Rapidflare] Invalid display.action.widget.overlay: must be a boolean when provided.');
		return false;
	}
	if (widget.fullWidthOnMaximize !== undefined && typeof widget.fullWidthOnMaximize !== 'boolean') {
		void logger.error('[Rapidflare] Invalid display.action.widget.fullWidthOnMaximize: must be a boolean when provided.');
		return false;
	}
	if (widget.titleText !== undefined && typeof widget.titleText !== 'string') {
		void logger.error('[Rapidflare] Invalid display.action.widget.titleText: must be a string when provided.');
		return false;
	}
	if (widget.titleLogo !== undefined && typeof widget.titleLogo !== 'string') {
		void logger.error('[Rapidflare] Invalid display.action.widget.titleLogo: must be a string when provided.');
		return false;
	}
	return true;
};

const validateOptionalDisplayString = <TDisplayConfig extends RfEmbedSdkDisplayConfig, FieldKey extends RfEmbedSdkOptionalStringFieldKey<TDisplayConfig>>(displayTypeLabel: RfEmbedSdkDisplayType, display: TDisplayConfig, fieldName: FieldKey): boolean => {
	const value = display[fieldName];
	if (value !== undefined && typeof value !== 'string') {
		void logger.error(`[Rapidflare] Invalid display.${String(fieldName)} for type "${displayTypeLabel}": must be a string when provided.`);
		return false;
	}
	return true;
};

const validateOptionalDisplayBoolean = <TDisplayConfig extends RfEmbedSdkDisplayConfig, FieldKey extends RfEmbedSdkOptionalBooleanFieldKey<TDisplayConfig>>(displayTypeLabel: RfEmbedSdkDisplayType, display: TDisplayConfig, fieldName: FieldKey): boolean => {
	const value = display[fieldName];
	if (value !== undefined && typeof value !== 'boolean') {
		void logger.error(`[Rapidflare] Invalid display.${String(fieldName)} for type "${displayTypeLabel}": must be a boolean when provided.`);
		return false;
	}
	return true;
};

export const mergeRfEmbedSdkInstanceConfig = (atomConfig: RfEmbedSdkConfig | undefined, contextConfig: RfEmbedSdkConfig | undefined, overrideConfig?: RfEmbedSdkInstanceOverrideConfig): RfEmbedSdkConfig => {
	return deepmerge<RfEmbedSdkConfig>(
		deepmerge<RfEmbedSdkConfig>(atomConfig ?? ({} as RfEmbedSdkConfig), contextConfig ?? ({} as RfEmbedSdkConfig), { clone: true }),
		overrideConfig ?? ({} as RfEmbedSdkInstanceOverrideConfig),
		{ clone: true }
	);
};
